import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { tenantBasePath, tenantPath } from './lib/tenantPath';
import html2canvas from 'html2canvas';
import './App.css';
import './polish.css';
import CompanyInput from './components/CompanyInput';
import SignalReview from './components/SignalReview';
import ScoutDegradedBanner from './components/ScoutDegradedBanner';
import AnalysisResults from './components/AnalysisResults';
import FixerResults from './components/FixerResults';
import FitResults from './components/FitResults';
import PipelineDashboard from './components/PipelineDashboard';
import TrackerDashboard from './components/TrackerDashboard';
import WebhookManager from './components/WebhookManager';
import AppHeader from './components/layout/AppHeader';
import SaveInvestigationButton from './components/SaveInvestigationButton';
import ProviderHealthBanner from './components/ProviderHealthBanner';
import TrialBanner from './components/TrialBanner';
import PasswordRotationBanner from './components/PasswordRotationBanner';
import { useAuth } from './context/AuthContext';
import { formatBackendDetail } from './lib/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getApiKey = () => localStorage.getItem('quod_api_key') || '';

const STEPS = [
  { id: 'company', label: 'Scout' },
  { id: 'signals', label: 'Review' },
  { id: 'analysis', label: 'Analyse' },
  { id: 'fixer', label: 'Fix' },
  { id: 'fit', label: 'Fit' },
];

function App() {
  const { user, logout, hasPermission } = useAuth();
  const canScan = hasPermission('scan:run');
  const location = useLocation();
  const navigate = useNavigate();
  // Pick up intended tab from ?view= on first mount (e.g. when arriving from
  // /settings via Tracker nav click).
  const initialView = new URLSearchParams(location.search).get('view');
  const [currentStep, setCurrentStep] = useState('company');
  const [showPipeline, setShowPipeline] = useState(initialView === 'pipeline' || (!canScan && !initialView));
  const [showTracker, setShowTracker] = useState(initialView === 'tracker');
  const [showWebhooks, setShowWebhooks] = useState(initialView === 'webhooks');
  const [apiKey, setApiKey] = useState(getApiKey());
  const [companyData, setCompanyData] = useState(null);
  const [candidates, setCandidates] = useState([]);
  // Snapshot evidence comes from the persisted Signal records, not the stale
  // pending Scout cards. Approval can succeed even when a later Analyse fails.
  const [approvedSignals, setApprovedSignals] = useState([]);
  const [reviewNeedsReadback, setReviewNeedsReadback] = useState(false);
  const reviewMutationRef = useRef(false);
  const [lowSignalMode, setLowSignalMode] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('');
  const [duplicatesSuppressed, setDuplicatesSuppressed] = useState(0);
  // Provider-degradation surface coming back from /api/scout responses.
  // `scoutWarnings` is a list of {kind, admin_message, user_message, details};
  // `scoutDegraded` is true whenever any LLM provider failed over to the
  // deterministic classifier. Both reset on every new run.
  const [scoutWarnings, setScoutWarnings] = useState([]);
  const [scoutDegraded, setScoutDegraded] = useState(false);
  const [scoutDiagnostics, setScoutDiagnostics] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [fixerResult, setFixerResult] = useState(null);
  const [fitResult, setFitResult] = useState(null);
  const [analysisLens, setAnalysisLens] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [batchJob, setBatchJob] = useState(null);
  const pollRef = useRef(null);

  // Pick up ?view= on first mount AND any subsequent URL change (e.g. when
  // GlobalSearch navigates to /?view=pipeline&company=<id> while the user is
  // already on /). Without reacting to `location.search`, an in-place URL
  // change would never flip the active tab.
  useEffect(() => {
    const v = new URLSearchParams(location.search).get('view');
    if (v === 'pipeline') {
      setShowPipeline(true); setShowTracker(false); setShowWebhooks(false);
    } else if (v === 'tracker') {
      setShowPipeline(false); setShowTracker(true); setShowWebhooks(false);
    } else if (v === 'webhooks') {
      setShowPipeline(false); setShowTracker(false); setShowWebhooks(true);
    } else if (v === 'home') {
      setShowPipeline(false); setShowTracker(false); setShowWebhooks(false);
    }
    // Strip ?view= once consumed so reload doesn't keep forcing the tab.
    // Preserve other params (e.g. ?company=<id>) so child panels can consume them.
    // Iter 38c: preserve the tenant base path (/t/<slug>) so this cleanup
    // does NOT strip multi-tenant context.
    if (v && location.search) {
      const params = new URLSearchParams(location.search);
      params.delete('view');
      const q = params.toString();
      const base = tenantBasePath(location.pathname) || '/';
      navigate(q ? `${base}?${q}` : base, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Listen for empty-state CTAs that want to jump the user back to Scout.
  useEffect(() => {
    const goHome = () => {
      setShowPipeline(false); setShowTracker(false); setShowWebhooks(false);
      setCurrentStep('company');
    };
    window.addEventListener('quod:go-home', goHome);
    return () => window.removeEventListener('quod:go-home', goHome);
  }, []);

  const apiHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'X-API-Key': apiKey,
  }), [apiKey]);

  const handleSetApiKey = (key) => {
    setApiKey(key);
    localStorage.setItem('quod_api_key', key);
  };

  // Run Scout (background job with polling)
  const handleRunScout = async (formData) => {
    setLoading(true);
    setError(null);
    setShowPipeline(false);
    setApprovedSignals([]);
    setScoutDiagnostics(null);
    setScoutWarnings([]);
    setScoutDegraded(false);
    try {
      const resp = await fetch(`${API_URL}/api/scout`, {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify(formData),
      });
      const text = await resp.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(`Scout failed (${resp.status}): Server returned non-JSON response.`); }
      if (!resp.ok) throw new Error(formatBackendDetail(data.detail) || `Scout failed: ${resp.status}`);

      // Poll for job completion
      const jobId = data.job_id;
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const pollResp = await fetch(`${API_URL}/api/scout/job/${jobId}`, { headers: apiHeaders() });
          if (!pollResp.ok) continue;
          const pollData = await pollResp.json();
          if (pollData.status === 'done') {
            const result = pollData.result;
            setCompanyData({ id: result.company_id, name: result.company_name, ...formData });
            setCandidates(result.candidates);
            setLowSignalMode(result.low_signal_mode || false);
            setVerificationStatus(result.verification_status || '');
            setDuplicatesSuppressed(result.duplicates_suppressed || 0);
            // Provider-degradation warnings (e.g. OpenAI quota exhausted) —
            // surfaced as a calm advisory above the signals review table.
            setScoutWarnings(result.warnings || []);
            setScoutDegraded(!!result.degraded);
            setScoutDiagnostics({ search: result.search_diagnostics,
              classification: result.classification_diagnostics });
            setCompletedSteps(prev => [...new Set([...prev, 'company'])]);
            setCurrentStep('signals');
            return;
          }
          if (pollData.status === 'failed') {
            throw new Error(pollData.error || 'Scout failed');
          }
        } catch (e) {
          if (e.message && !e.message.includes('Failed to fetch')) throw e;
        }
      }
      throw new Error('Scout timed out after 3 minutes. Try again.');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // Run Batch Scout
  const handleRunBatchScout = async (companies) => {
    setLoading(true);
    setError(null);
    setShowPipeline(false);
    try {
      const resp = await fetch(`${API_URL}/api/scout/batch`, {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify({ companies }),
      });
      if (!resp.ok) { const text = await resp.text(); let detail; try { detail = JSON.parse(text).detail; } catch {} throw new Error(formatBackendDetail(detail) || `Batch scout failed: ${resp.status}`); }
      const data = await resp.json();
      setBatchJob({ jobId: data.job_id, total: data.total, completed: 0, failed: 0, status: 'running', results: [], errors: [], companyNames: data.company_names });
    } catch (e) { setError(e.message); setLoading(false); }
  };

  // Poll batch job status
  useEffect(() => {
    if (!batchJob || batchJob.status === 'done') {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (batchJob?.status === 'done') setLoading(false);
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const resp = await fetch(`${API_URL}/api/scout/batch/${batchJob.jobId}`, { headers: apiHeaders() });
        if (!resp.ok) return;
        const data = await resp.json();
        setBatchJob(prev => ({
          ...prev,
          completed: data.completed,
          failed: data.failed,
          status: data.status,
          results: data.results || [],
          errors: data.errors || [],
        }));
      } catch {}
    }, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [batchJob?.jobId, batchJob?.status]);

  const readReviewState = async (companyId, includeApproved = false) => {
    const pendingResponse = await fetch(`${API_URL}/api/scout/candidates/${companyId}`, { headers: apiHeaders() });
    if (!pendingResponse.ok) throw new Error('Review changed, but its saved state could not be refreshed. Reopen the company before submitting again.');
    const pending = await pendingResponse.json();
    if (!Array.isArray(pending)) throw new Error('Review readback returned an invalid candidate list.');
    setCandidates(pending);
    if (includeApproved) {
      const approvedResponse = await fetch(`${API_URL}/api/signals/company/${companyId}?window_days=90`, { headers: apiHeaders() });
      if (!approvedResponse.ok) throw new Error('Signals were approved, but saved evidence could not be read. Reopen the company before submitting again.');
      const records = await approvedResponse.json();
      if (!Array.isArray(records) || records.some(record => record.company_id !== companyId)) {
        throw new Error('Approved evidence readback did not match this company.');
      }
      setApprovedSignals(records);
    }
  };

  // Persist rejection separately from the reversible Deselect All control.
  const handleRejectSignals = async (selectedIds) => {
    if (loading || reviewMutationRef.current || reviewNeedsReadback || !companyData?.id || !selectedIds.length) return;
    reviewMutationRef.current = true;
    setReviewNeedsReadback(true);
    setLoading(true); setError(null);
    try {
      const response = await fetch(`${API_URL}/api/scout/reject`, {
        method: 'POST', headers: apiHeaders(),
        body: JSON.stringify({ company_id: companyData.id, candidate_ids: selectedIds }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(formatBackendDetail(data.detail) || 'Rejection failed');
      }
      await readReviewState(companyData.id);
      setReviewNeedsReadback(false);
    } catch (e) { setError(e.message); }
    finally { reviewMutationRef.current = false; setLoading(false); }
  };

  const handleReadReviewState = async () => {
    if (loading || reviewMutationRef.current || !companyData?.id) return;
    setLoading(true); setError(null);
    try {
      await readReviewState(companyData.id, true);
      setReviewNeedsReadback(false);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // Approve + Analyse. Read back the actual stored evidence before continuing.
  const handleApproveSignals = async (selectedIds, edits) => {
    if (loading || reviewMutationRef.current || reviewNeedsReadback || !companyData?.id || !selectedIds.length) return;
    reviewMutationRef.current = true;
    setReviewNeedsReadback(true);
    setLoading(true);
    setError(null);
    try {
      const approveResp = await fetch(`${API_URL}/api/scout/approve`, {
        method: 'POST', headers: apiHeaders(),
        body: JSON.stringify({ company_id: companyData.id, candidate_ids: selectedIds, edits }),
      });
      if (!approveResp.ok) { const text = await approveResp.text(); let detail; try { detail = JSON.parse(text).detail; } catch {} throw new Error(formatBackendDetail(detail) || 'Approval failed'); }

      await readReviewState(companyData.id, true);
      setReviewNeedsReadback(false);

      const analysisResp = await fetch(`${API_URL}/api/analysis`, {
        method: 'POST', headers: apiHeaders(),
        body: JSON.stringify({
          company_id: companyData.id,
          window_days: 90,
          vector: companyData.vector || null,
          sub_vector: companyData.sub_vector || null,
          analysis_lens: analysisLens || null,
        }),
      });
      if (!analysisResp.ok) { const text = await analysisResp.text(); let detail; try { detail = JSON.parse(text).detail; } catch {} throw new Error(formatBackendDetail(detail) || 'Analysis failed'); }
      const analysis = await analysisResp.json();
      setAnalysisResult(analysis);
      setCompletedSteps(prev => [...new Set([...prev, 'signals'])]);
      setCurrentStep('analysis');
    } catch (e) { setError(e.message); }
    finally { reviewMutationRef.current = false; setLoading(false); }
  };

  // Generate Fixer roadmap (background job with polling)
  const handleGenerateRoadmap = async () => {
    if (!analysisResult) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API_URL}/api/fixer/generate`, {
        method: 'POST', headers: apiHeaders(),
        body: JSON.stringify({ analysis_id: analysisResult.id }),
      });
      const text = await resp.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error('Roadmap generation timed out. Please try again.'); }
      if (!resp.ok) throw new Error(formatBackendDetail(data.detail) || 'Fixer failed');

      // If it returned a job_id, poll for completion
      if (data.job_id) {
        const jobId = data.job_id;
        for (let i = 0; i < 60; i++) {
          await new Promise(r => setTimeout(r, 3000));
          try {
            const pollResp = await fetch(`${API_URL}/api/fixer/job/${jobId}`, { headers: apiHeaders() });
            if (!pollResp.ok) continue;
            const pollData = await pollResp.json();
            if (pollData.status === 'done' && pollData.result) {
              setFixerResult(pollData.result);
              setCompletedSteps(prev => [...new Set([...prev, 'analysis'])]);
              setCurrentStep('fixer');
              return;
            }
            if (pollData.status === 'failed') {
              throw new Error(pollData.error || 'Roadmap generation failed');
            }
          } catch (e) {
            if (e.message && !e.message.includes('Failed to fetch')) throw e;
          }
        }
        throw new Error('Roadmap generation timed out after 3 minutes.');
      } else {
        // Direct response (legacy)
        setFixerResult(data);
        setCompletedSteps(prev => [...new Set([...prev, 'analysis'])]);
        setCurrentStep('fixer');
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // Generate Fit assessment
  const handleGenerateFit = async () => {
    if (!analysisResult) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API_URL}/api/fit/generate`, {
        method: 'POST', headers: apiHeaders(),
        body: JSON.stringify({ analysis_id: analysisResult.id }),
      });
      if (!resp.ok) { const text = await resp.text(); let detail; try { detail = JSON.parse(text).detail; } catch {} throw new Error(formatBackendDetail(detail) || 'Fit generation failed'); }
      const data = await resp.json();
      setFitResult(data);
      setCompletedSteps(prev => [...new Set([...prev, 'fixer', 'fit'])]);
      setCurrentStep('fit');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // Export state
  const [exportImage, setExportImage] = useState(null);
  const [exportPdf, setExportPdf] = useState(null);

  // PNG Export — render inline so user can right-click save
  const handleDownloadAnalysisPNG = async () => {
    const el = document.getElementById('analysis-panel');
    if (!el) return;
    setError(null);
    try {
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      setExportImage({
        dataUrl: canvas.toDataURL('image/png'),
        filename: `QUOD-Finder_Analysis_${analysisResult?.company_name?.replace(/\s+/g, '_') || 'report'}.png`,
      });
    } catch (e) { setError('Export failed: ' + e.message); }
  };

  // PDF Export — fetch from backend, convert to data URL for inline display
  const handleDownloadAnalysisPDF = async () => {
    if (!analysisResult?.id) return;
    setError(null);
    try {
      const resp = await fetch(
        `${API_URL}/api/export/analysis/${analysisResult.id}${apiKey ? `?api_key=${encodeURIComponent(apiKey)}` : ''}`,
        { credentials: 'include' },
      );
      if (!resp.ok) {
        if (resp.status === 401) throw new Error('Session expired. Please sign in again.');
        throw new Error(`PDF export failed: ${resp.status}`);
      }
      const blob = await resp.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        setExportPdf({
          dataUrl: reader.result,
          filename: `QUOD-Finder_Analysis_${analysisResult.company_name?.replace(/\s+/g, '_') || 'report'}.pdf`,
        });
      };
      reader.readAsDataURL(blob);
    } catch (e) { setError('PDF export failed: ' + e.message); }
  };

  // PDF Export for Fixer roadmap
  const handleDownloadRoadmapPDF = async () => {
    if (!fixerResult?.id) return;
    setError(null);
    try {
      const resp = await fetch(
        `${API_URL}/api/export/roadmap/${fixerResult.id}${apiKey ? `?api_key=${encodeURIComponent(apiKey)}` : ''}`,
        { credentials: 'include' },
      );
      if (!resp.ok) {
        if (resp.status === 401) throw new Error('Session expired. Please sign in again.');
        throw new Error(`PDF export failed: ${resp.status}`);
      }
      const blob = await resp.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        setExportPdf({
          dataUrl: reader.result,
          filename: `QUOD-Fixer_Roadmap_${fixerResult.company_name?.replace(/\s+/g, '_') || 'roadmap'}.pdf`,
        });
      };
      reader.readAsDataURL(blob);
    } catch (e) { setError('PDF export failed: ' + e.message); }
  };

  const handleDownloadRoadmapPNG = async () => {
    const el = document.getElementById('fixer-panel');
    if (!el) return;
    setError(null);
    try {
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      setExportImage({
        dataUrl: canvas.toDataURL('image/png'),
        filename: `QUOD-Fixer_Roadmap_${fixerResult?.company_name?.replace(/\s+/g, '_') || 'roadmap'}.png`,
      });
    } catch (e) { setError('Export failed: ' + e.message); }
  };

  // Fit PNG Export
  const handleDownloadFitPNG = async () => {
    const el = document.getElementById('fit-panel');
    if (!el) return;
    setError(null);
    try {
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      setExportImage({
        dataUrl: canvas.toDataURL('image/png'),
        filename: `QUOD-Finder_Fit_${fitResult?.company_name?.replace(/\s+/g, '_') || 'fit'}.png`,
      });
    } catch (e) { setError('Export failed: ' + e.message); }
  };

  // Fit PDF Export
  const handleDownloadFitPDF = async () => {
    if (!analysisResult?.id) return;
    setError(null);
    try {
      const resp = await fetch(
        `${API_URL}/api/export/fit/${analysisResult.id}${apiKey ? `?api_key=${encodeURIComponent(apiKey)}` : ''}`,
        { credentials: 'include' },
      );
      if (!resp.ok) {
        if (resp.status === 401) throw new Error('Session expired. Please sign in again.');
        throw new Error(`PDF export failed: ${resp.status}`);
      }
      const blob = await resp.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        setExportPdf({
          dataUrl: reader.result,
          filename: `QUOD-Finder_Fit_${fitResult?.company_name?.replace(/\s+/g, '_') || 'fit'}.pdf`,
        });
      };
      reader.readAsDataURL(blob);
    } catch (e) { setError('PDF export failed: ' + e.message); }
  };

  // Cleanup PDF export on close
  const closePdfExport = () => {
    setExportPdf(null);
  };

  // Re-scan (background job with polling)
  const handleRescan = async () => {
    if (!companyData?.id) return;
    setLoading(true);
    setError(null);
    setScoutWarnings([]);
    setScoutDegraded(false);
    setScoutDiagnostics(null);
    try {
      const resp = await fetch(`${API_URL}/api/scout/rescan`, {
        method: 'POST', headers: apiHeaders(),
        body: JSON.stringify({ company_id: companyData.id }),
      });
      const text = await resp.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(`Rescan failed (${resp.status})`); }
      if (!resp.ok) throw new Error(formatBackendDetail(data.detail) || `Rescan failed: ${resp.status}`);

      // Poll for job completion
      const jobId = data.job_id;
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const pollResp = await fetch(`${API_URL}/api/scout/job/${jobId}`, { headers: apiHeaders() });
          if (!pollResp.ok) continue;
          const pollData = await pollResp.json();
          if (pollData.status === 'done') {
            // Fetch fresh candidates
            const candidatesResp = await fetch(`${API_URL}/api/scout/candidates/${companyData.id}`, { headers: apiHeaders() });
            if (candidatesResp.ok) {
              setCandidates(await candidatesResp.json());
            }
            const result = pollData.result || {};
            setScoutWarnings(result.warnings || []);
            setScoutDegraded(!!result.degraded);
            setScoutDiagnostics({ search: result.search_diagnostics,
              classification: result.classification_diagnostics });
            setCurrentStep('signals');
            return;
          }
          if (pollData.status === 'failed') {
            throw new Error(pollData.error || 'Rescan failed');
          }
        } catch (e) {
          if (e.message && !e.message.includes('Failed to fetch')) throw e;
        }
      }
      throw new Error('Rescan timed out after 3 minutes.');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // Reset
  const handleReset = () => {
    setCurrentStep('company');
    setShowPipeline(false);
    setCompanyData(null);
    setCandidates([]);
    setApprovedSignals([]);
    setReviewNeedsReadback(false);
    setLowSignalMode(false);
    setScoutWarnings([]);
    setScoutDegraded(false);
    setScoutDiagnostics(null);
    setAnalysisResult(null);
    setFixerResult(null);
    setFitResult(null);
    setCompletedSteps([]);
    setError(null);
    setBatchJob(null);
  };

  const handleStepClick = (stepId) => {
    setShowPipeline(false);
    if (stepId === 'company') setCurrentStep('company');
    else if (stepId === 'signals' && completedSteps.includes('company')) setCurrentStep('signals');
    else if (stepId === 'analysis' && completedSteps.includes('signals')) setCurrentStep('analysis');
    else if (stepId === 'fixer' && completedSteps.includes('analysis')) setCurrentStep('fixer');
    else if (stepId === 'fit' && completedSteps.includes('fixer')) setCurrentStep('fit');
  };

  const activeNav = showPipeline ? 'pipeline'
    : showTracker ? 'tracker'
    : showWebhooks ? 'webhooks'
    : 'home';

  const handleNav = (dest) => {
    setShowPipeline(dest === 'pipeline');
    setShowTracker(dest === 'tracker');
    setShowWebhooks(dest === 'webhooks');
    if (dest === 'home') handleReset();
    // Clear the ?view= query once we've consumed it so reload behaves sanely.
    // Iter 38c: preserve the tenant base path on this cleanup.
    if (location.search) {
      const base = tenantBasePath(location.pathname) || '/';
      navigate(base, { replace: true });
    }
  };

  return (
    <>
      <AppHeader active={activeNav} onNavigate={handleNav} />
      <div className="console-container">
        {/* Trial / activation lifecycle banner — shown for trial / expired /
            suspended / cancelled. Admins get an "Open Settings" link. */}
        <TrialBanner />
        {/* Calm rotation prompt — only renders when /api/users/me reports
            force_password_change=true (i.e. tenant policy explicitly demands it). */}
        <PasswordRotationBanner />
        {/* Owner/Admin-only operational banner. Hidden silently for everyone else,
            and never blocks the rest of the dashboard if the health endpoint fails. */}
        <ProviderHealthBanner onOpenSettings={() => navigate(tenantPath(tenantBasePath(location.pathname), '/settings'))} />
        {/* Contextual action strip (company name + scan controls) */}
        {(companyData || hasPermission('scan:run')) && (
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              padding: '8px 0 12px', fontSize: 12,
            }}
            data-testid="contextual-actions"
          >
            {companyData && (
              <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>
                {companyData.name}
              </span>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              {companyData && hasPermission('scan:run') && (
                <button className="btn btn-outline btn-sm" onClick={handleRescan} disabled={loading} data-testid="rescan-button">
                  Re-scan
                </button>
              )}
              {hasPermission('scan:run') && (
                <button className="btn btn-outline btn-sm" onClick={handleReset} data-testid="reset-button">
                  New Scan
                </button>
              )}
            </div>
          </div>
        )}

      {/* Pipeline View */}
      {showPipeline ? (
        <PipelineDashboard
          apiKey={apiKey}
          onSelectCompany={(company) => {
            handleReset();
            setShowPipeline(false);
            setCompanyData(company);
            setCurrentStep('company');
          }}
        />
      ) : showTracker ? (
        <TrackerDashboard
          apiKey={apiKey}
          onSelectCompany={(company) => {
            handleReset();
            setShowTracker(false);
            setCompanyData(company);
            setCurrentStep('company');
          }}
        />
      ) : showWebhooks ? (
        <WebhookManager apiKey={apiKey} />
      ) : (
        <>
          {/* Steps */}
          <div className="step-indicator" data-testid="step-indicator">
            {STEPS.map((step) => (
              <div
                key={step.id}
                className={`step ${currentStep === step.id ? 'active' : ''} ${completedSteps.includes(step.id) && currentStep !== step.id ? 'completed' : ''}`}
                onClick={() => handleStepClick(step.id)}
                data-testid={`step-${step.id}`}
              >
                {step.label}
              </div>
            ))}
          </div>

          {/* Loading */}
          {loading && <div className="loading-bar" data-testid="loading-bar"><div className="loading-progress" /></div>}

          {/* Error */}
          {error && (
            <div className="panel" style={{ borderColor: 'var(--danger)', marginBottom: '12px' }}>
              <div className="panel-body" style={{ color: 'var(--danger)', fontSize: '13px' }} data-testid="error-message">{error}</div>
            </div>
          )}

          {/* Content */}
          {currentStep === 'company' && (
            <>
              <CompanyInput onSubmit={handleRunScout} onBatchSubmit={handleRunBatchScout} loading={loading} initialData={companyData} apiKey={apiKey} />
              {batchJob && (
                <div className="panel" style={{ marginTop: '12px' }} data-testid="batch-progress-panel">
                  <div className="panel-header">
                    Batch Scout Progress
                    <span className={`badge ${batchJob.status === 'done' ? 'badge-low' : 'badge-medium'}`} data-testid="batch-status">
                      {batchJob.status === 'done' ? 'Complete' : 'Running'}
                    </span>
                  </div>
                  <div className="panel-body">
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        <span>{batchJob.completed + batchJob.failed} / {batchJob.total} companies</span>
                        <span>{batchJob.failed > 0 ? `${batchJob.failed} failed` : ''}</span>
                      </div>
                      <div style={{ background: 'var(--border)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                        <div
                          style={{ height: '100%', borderRadius: '4px', background: 'var(--primary)', transition: 'width 0.5s', width: `${Math.round(((batchJob.completed + batchJob.failed) / batchJob.total) * 100)}%` }}
                          data-testid="batch-progress-bar"
                        />
                      </div>
                    </div>
                    {batchJob.results.length > 0 && (
                      <div className="data-table-wrap">
                        <table className="data-table" data-testid="batch-results-table">
                          <thead><tr><th>Company</th><th>Signals Found</th><th>Duration</th></tr></thead>
                          <tbody>
                            {batchJob.results.map((r, i) => (
                              <tr key={i}>
                                <td style={{ fontWeight: '600' }}>{r.company_name}</td>
                                <td><span className="badge badge-type">{r.candidate_count}</span></td>
                                <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{r.duration}s</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {batchJob.errors.length > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        {batchJob.errors.map((e, i) => (
                          <div key={i} style={{ fontSize: '12px', color: 'var(--danger)', padding: '4px 0' }}>
                            {e.company_name}: {e.error}
                          </div>
                        ))}
                      </div>
                    )}
                    {batchJob.status === 'done' && (
                      <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                        <button className="btn btn-primary btn-sm" onClick={() => { setBatchJob(null); setShowPipeline(true); }} data-testid="batch-view-pipeline">
                          View Pipeline
                        </button>
                        <button className="btn btn-outline btn-sm" onClick={() => setBatchJob(null)} data-testid="batch-dismiss">
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {currentStep === 'signals' && (
            <>
              <ScoutDegradedBanner warnings={scoutWarnings} degraded={scoutDegraded} diagnostics={scoutDiagnostics} />
              {reviewNeedsReadback && !loading && <div className="panel" role="status" data-testid="review-readback-required">
                <div className="panel-body">Review submission has not been reconciled. Inspect the saved review state before submitting again. <button type="button" className="btn btn-outline btn-sm" onClick={handleReadReviewState}>Inspect saved review state</button></div>
              </div>}
              <SignalReview candidates={candidates} companyName={companyData?.name || ''} onApprove={handleApproveSignals} onReject={handleRejectSignals} loading={loading || reviewNeedsReadback} lowSignalMode={lowSignalMode} verificationStatus={verificationStatus} duplicatesSuppressed={duplicatesSuppressed} analysisLens={analysisLens} onLensChange={setAnalysisLens} />
            </>
          )}

          {currentStep === 'analysis' && analysisResult && (
            <div id="analysis-panel">
              <AnalysisResults
                analysis={analysisResult}
                onReset={handleReset}
                onGenerateRoadmap={handleGenerateRoadmap}
                onDownloadPDF={handleDownloadAnalysisPDF}
                onDownloadPNG={handleDownloadAnalysisPNG}
                loading={loading}
              />
            </div>
          )}

          {currentStep === 'fixer' && fixerResult && (
            <div id="fixer-panel">
              <FixerResults
                roadmap={fixerResult}
                onBack={() => setCurrentStep('analysis')}
                onDownloadPDF={handleDownloadRoadmapPDF}
                onDownloadPNG={handleDownloadRoadmapPNG}
                onGenerateFit={handleGenerateFit}
                fitLoading={loading}
              />
              <SaveInvestigationButton
                companyData={companyData}
                analysisLens={analysisLens}
                candidates={candidates}
                approvedSignals={approvedSignals}
                analysisResult={analysisResult}
                fixerResult={fixerResult}
                fitResult={null}
              />
            </div>
          )}

          {currentStep === 'fit' && fitResult && (
            <div id="fit-panel">
              <FitResults
                fit={fitResult}
                analysisId={analysisResult?.id}
                onBack={() => setCurrentStep('fixer')}
                onDownloadPDF={handleDownloadFitPDF}
                onDownloadPNG={handleDownloadFitPNG}
              />
              <SaveInvestigationButton
                companyData={companyData}
                analysisLens={analysisLens}
                candidates={candidates}
                approvedSignals={approvedSignals}
                analysisResult={analysisResult}
                fixerResult={fixerResult}
                fitResult={fitResult}
              />
            </div>
          )}
        </>
      )}
      {/* PNG Export overlay */}
      {exportImage && (
        <div
          data-testid="export-overlay"
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.7)', display: 'flex',
            flexDirection: 'column', alignItems: 'center',
            padding: '24px', overflow: 'auto',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setExportImage(null); }}
        >
          <div style={{ background: 'var(--surface)', borderRadius: '8px', padding: '16px', maxWidth: '900px', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontWeight: '600', fontSize: '14px' }}>Export: {exportImage.filename}</span>
              <button className="btn btn-outline btn-sm" onClick={() => setExportImage(null)} data-testid="export-close">Close</button>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
              Right-click the image below and select "Save image as..." to download.
            </p>
            <img
              src={exportImage.dataUrl}
              alt={exportImage.filename}
              data-testid="export-image"
              style={{ width: '100%', border: '1px solid var(--border)', borderRadius: '4px' }}
            />
          </div>
        </div>
      )}

      {/* PDF Export overlay */}
      {exportPdf && (
        <div
          data-testid="pdf-export-overlay"
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.7)', display: 'flex',
            flexDirection: 'column', alignItems: 'center',
            padding: '24px', overflow: 'auto',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closePdfExport(); }}
        >
          <div style={{ background: 'var(--surface)', borderRadius: '8px', padding: '16px', maxWidth: '900px', width: '100%', height: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontWeight: '600', fontSize: '14px' }}>{exportPdf.filename}</span>
              <button className="btn btn-outline btn-sm" onClick={closePdfExport} data-testid="pdf-export-close">Close</button>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
              Use the PDF viewer controls to save or print.
            </p>
            <iframe
              src={exportPdf.dataUrl}
              title={exportPdf.filename}
              data-testid="pdf-export-frame"
              style={{ flex: 1, width: '100%', border: '1px solid var(--border)', borderRadius: '4px' }}
            />
          </div>
        </div>
      )}

      {/* Global footer — text-only, consistent across every page. */}
      <footer className="app-powered-footer" data-testid="global-footer">
        <span className="app-powered-footer-copy">© 2026 QUOD-Tech Ltd. All rights reserved.</span>
        <span className="app-powered-footer-sep" aria-hidden="true">·</span>
        <span className="app-powered-footer-mark">POWERED BY QUOD-TECH</span>
      </footer>
    </div>
    </>
  );
}

export default App;
